import { useEffect } from 'react'
import { useContentfulResourcesMapped } from 'stores/Contentful/hooks'
import { Hero } from 'shared/layout/Hero'
import { FlexBox, Stack } from 'ui/primitives'
import { Heading, Typography } from 'ui/typography'
import { ResourceUtils } from 'services/ContentfulService/models/resource/utils'
import { Container } from 'shared/layout/Container'
import { RichText } from 'components/vendor/Contentful/richText/RichTextRenderer'
import { AsideItem } from '../shared/Aside/AsideItem'
import { aside, textContainer } from './ContactUs.css'
import { InlineSvg } from 'components/patterns/icons/svg-tools/InlineSvg'

interface GlobalScope {
	_fo?: any[]
	s?: HTMLScriptElement
}

export const ContactUs = () => {
	const pageResources = useContentfulResourcesMapped()
	const contactMainContent = ResourceUtils(pageResources['contact.section_1'])
	const contactAfterContent = ResourceUtils(pageResources['contact.section_2'])

	useEffect(() => {
		const globalScope = window as GlobalScope
		globalScope._fo = globalScope._fo || [{ d: 'center', c: 'fa474b', i: 37760 }]
		globalScope.s = document.createElement('script')
		globalScope.s.type = 'text/javascript'
		globalScope.s.src = ('https:' === document.location.protocol ? 'https://' : 'http://') + 'formcrafts.com/js/fc.js'
		document.body.appendChild(globalScope.s)

		return (): void => {
			if (globalScope.s) {
				document.body.removeChild(globalScope.s)
			}
		}
	}, [])

	const InlineSvgAsideIcon = ({ icon }: { icon: string }) => (
		<InlineSvg asset={ResourceUtils(pageResources['contact.aside.' + icon]).getMediaContent()} width={24} height={24} />
	)

	return (
		<>
			<Hero>
				<Heading.H1>{ResourceUtils(pageResources['contact.title']).getPlainTextContent()}</Heading.H1>
			</Hero>

			<Container>
				<FlexBox
					paddingY={{ xs: 'lg', md: 'xxl' }}
					gap={{ xs: 'lg', md: 'xxl' }}
					justifyContent="space-between"
					flexDirection={{ xs: 'column', md: 'row' }}
				>
					<Stack gap="xxl" className={textContainer}>
						<Stack gap="md">
							<Heading.H2>{contactMainContent.getPlainTextContent()}</Heading.H2>
							<RichText document={contactMainContent.getRichContent()} />
						</Stack>
						<Stack gap="md">
							<Heading.H2>{contactAfterContent.getPlainTextContent()}</Heading.H2>
							<RichText document={contactAfterContent.getRichContent()} />
						</Stack>
						<div id="fa474b">
							<a href="https://formcrafts.com/a/37760">Fiona Contact</a>
						</div>
					</Stack>

					<Stack gap="lg" className={aside}>
						<Stack gap="sm">
							<Heading.H3>{ResourceUtils(pageResources['contact.aside.title']).getPlainTextContent()}</Heading.H3>
							<Typography>{ResourceUtils(pageResources['contact.aside.description']).getPlainTextContent()}</Typography>
						</Stack>
						<AsideItem
							icon={<InlineSvgAsideIcon icon="email" />}
							title={ResourceUtils(pageResources['contact.aside.email']).getPlainTextContent()}
							link={{
								text: ResourceUtils(pageResources['contact.aside.email_value']).getPlainTextContent(),
								href: `mailto:${ResourceUtils(pageResources['contact.aside.email_value']).getPlainTextContent()}`
							}}
						/>
						<AsideItem
							icon={<InlineSvgAsideIcon icon="phone" />}
							title={ResourceUtils(pageResources['contact.aside.phone']).getPlainTextContent()}
							link={{
								text: ResourceUtils(pageResources['contact.aside.phone_value']).getPlainTextContent(),
								href: `tel:${ResourceUtils(pageResources['contact.aside.phone_value']).getPlainTextContent()}`
							}}
						/>
						<AsideItem
							icon={<InlineSvgAsideIcon icon="location" />}
							title={ResourceUtils(pageResources['contact.aside.location']).getPlainTextContent()}
							richTextValue={ResourceUtils(pageResources['contact.aside.location_value']).getRichContent()}
						/>
					</Stack>
				</FlexBox>
			</Container>
		</>
	)
}
